export interface LessonBody {
  explanation: string[];
  caseTitle: string;
  caseBody: string[];
  afterCase?: string[];
  takeaway: string;
}

export const MODULE1_LESSON_BODIES: Record<string, LessonBody> = {
  'm1-l1': {
    explanation: [
      "Nobody who's been scammed thinks of themselves as gullible — and they're right not to. The people who fall for a well-run scam are, on average, no less careful than anyone else. What they were missing wasn't caution. It was a name for what was happening to them while it was still happening.",
    ],
    caseTitle: 'Why this module exists',
    caseBody: [
      "A scam doesn't feel like a scam from the inside. It feels like an unusually busy Tuesday: a vendor with a small billing question, a boss who needs one thing before a flight, a bank flagging something on your account. Each of those, on its own, is completely ordinary — which is exactly why it works.",
      "This module isn't a list of scams to memorize. It's the one underlying pattern that every scam in this curriculum — phishing, wire fraud, impersonation, AI voice cloning — is a variation of. Learn the pattern once, and you'll recognize it faster than any list of examples could teach you to.",
    ],
    takeaway:
      "Being targeted is not a judgment on you. Your contact details are public because your job requires it — that's the only thing every target of this module has in common.",
  },
  'm1-l2': {
    explanation: [
      'Every scam that has ever worked pulled on some combination of four levers. Learn to name them in the moment, and you can spot a scam before you know which specific scam it is.',
    ],
    caseTitle: 'The four levers',
    caseBody: [
      'Urgency — you have to decide now, before you have time to check. Authority — the request comes from (or claims to come from) someone you\'re not supposed to question. Isolation — you\'re asked to handle it alone, off the normal channel, without looping in the person who\'d normally catch it. Plausibility — nothing about the specific ask is unusual for your job, so it doesn\'t trigger suspicion on its own.',
      "Most real scams pull on two or three of these at once. A fake wire-instruction email is urgent (the closing is today), authoritative (it looks like it's from the coordinator), and plausible (wire instructions do go out on Thursdays) all at the same time.",
    ],
    takeaway:
      'When a request feels slightly uncomfortable to slow down and double-check, that discomfort is data — not rudeness.',
  },
  'm1-l3': {
    explanation: [
      "Most people meet a scam in the middle. A message arrives, it's a little strange, they make a decision in about eight seconds and move on with their day. What they never see is the weeks of work that happened before that message was sent — which is exactly why it worked.",
      "So we're going to walk one all the way through. From the moment a name gets pulled off a public listing to the moment the money leaves. Once you've watched it happen slowly, you'll start noticing the early parts in real time.",
    ],
    caseTitle: 'What this looked like',
    caseBody: [
      "A title company's email account was compromised six weeks before anything visible happened. The attacker didn't send a thing. They read. They learned which closings were pending, how the closing coordinator signed off her emails, and that wire instructions usually went out on Thursday afternoons.",
      "On the Thursday before a $410,000 closing, a message arrived from an address one character different from the coordinator's. It referenced the correct file number, the correct buyer, and the correct amount. It carried updated wire instructions and a short apology for the change. It was answered in four minutes.",
    ],
    afterCase: [
      "Notice what the sender never did. They never asked for anything unusual. Every individual step was something this person does on a normal Tuesday — and that's the whole point. A request that looks abnormal gets checked. A request that looks routine does not.",
      "The six weeks of quiet reading is what bought them that. They weren't guessing at what would sound normal. They had watched normal happen, dozens of times, and then reproduced it.",
    ],
    takeaway:
      "The scam isn't the message. The message is the last step. If you can name what the sender needs from you, you can usually name the scam before you've finished reading it.",
  },
  'm1-l4': {
    explanation: [
      "You don't need to become suspicious of everything. You need exactly one habit, applied consistently, at exactly the moment money or access is about to move.",
    ],
    caseTitle: 'The habit',
    caseBody: [
      "Before any payment or access change goes out based on an emailed or texted instruction, verify it through a channel the sender didn't choose for you — a phone number you already had on file, not one in the message asking for the change.",
      "This single habit is the two-minute step that stops nearly every wire-fraud and account-change scam in this curriculum, regardless of how convincing the message itself is. It doesn't require judging whether a message looks fake. It just requires never trusting the channel a request arrived on to also verify that same request.",
    ],
    takeaway:
      "One habit, applied every time money moves: verify the request through a channel the request itself didn't provide.",
  },
};

export const MODULE1_KNOWLEDGE_CHECK = {
  question:
    "A supplier emails to say their bank details have changed. What's the first thing you do?",
  options: [
    'Reply to the email and ask them to confirm',
    'Call the number in the email signature',
    'Call the number you already had on file for them',
    'Check whether the email address matches previous ones',
  ],
  correctIndex: 2,
};
